import type { ListRelationship } from "@/types/list-relationship";
import { PUSH_REASON, type FieldPushBlockReasonType } from "@/lib/documents/push-reason-codes";

export interface ResolvedFieldInfo {
  erp_id: string;
  list_slug: string;
  data?: Record<string, unknown>;
  label?: string;
  name?: string;
}

export interface PushBlockReason {
  // Declared in documents/push-reason-codes.ts, which maps each type to the
  // hold it causes (document-holds.ts). Includes `hidden_list_item`: a push
  // refused because the document's subsidiary was hidden by an admin
  // visibility policy (see documents/push-block-reasons), a first-class member
  // so exhaustive consumers cannot silently miss it.
  type: FieldPushBlockReasonType;
  field: string;
  field_label: string;
  value: string;
  value_name?: string;
  parent_field?: string;
  parent_label?: string;
  parent_value?: string;
  parent_value_name?: string;
  message: string;
  line_index?: number;
  line_description?: string;
}

/**
 * Check if a single list item is compatible with a selected parent value.
 */
export function checkItemCompatibility(
  itemData: Record<string, unknown>,
  parentValue: string | undefined,
  relationship: ListRelationship
): "compatible" | "incompatible" | "unknown" {
  if (!parentValue) return "unknown";

  const foreignKeyValue = itemData[relationship.foreign_key] as string | null | undefined;
  if (!foreignKeyValue) return "compatible"; // unrestricted item

  if (relationship.value_type === "multi_member") {
    const values = foreignKeyValue.split(",").map(s => s.trim());
    return values.includes(parentValue) ? "compatible" : "incompatible";
  }

  // single exact match
  return foreignKeyValue === parentValue ? "compatible" : "incompatible";
}

/**
 * Validate that all resolved fields are compatible with their parent fields
 * based on declared list relationships. Used at push time for hard blocking
 * and in the UI for warnings.
 */
export function validateFieldCompatibility(
  resolvedFields: Record<string, ResolvedFieldInfo>,
  relationships: ListRelationship[]
): { valid: boolean; violations: PushBlockReason[] } {
  const violations: PushBlockReason[] = [];

  for (const relationship of relationships) {
    // Find the child field (e.g., department_id for source_list "departments")
    const childFieldKey = Object.keys(resolvedFields).find(
      key => resolvedFields[key].list_slug === relationship.source_list
    );
    if (!childFieldKey) continue;

    // Find the parent field (e.g., subsidiary_id for target_list "subsidiaries")
    const parentFieldKey = Object.keys(resolvedFields).find(
      key => resolvedFields[key].list_slug === relationship.target_list
    );
    if (!parentFieldKey) continue; // parent not resolved — can't validate

    const childField = resolvedFields[childFieldKey];
    const parentField = resolvedFields[parentFieldKey];

    if (!childField.data) continue; // no data to check

    const result = checkItemCompatibility(childField.data, parentField.erp_id, relationship);
    if (result === "incompatible") {
      const childLabel = childField.label || childFieldKey;
      const parentLabel = parentField.label || parentFieldKey;
      const childDisplay = childField.name ? `${childField.name} (${childField.erp_id})` : childField.erp_id;
      const parentDisplay = parentField.name ? `${parentField.name} (${parentField.erp_id})` : parentField.erp_id;
      violations.push({
        type: PUSH_REASON.FIELD_INCOMPATIBILITY,
        field: childFieldKey,
        field_label: childLabel,
        value: childField.erp_id,
        value_name: childField.name,
        parent_field: parentFieldKey,
        parent_label: parentLabel,
        parent_value: parentField.erp_id,
        parent_value_name: parentField.name,
        message: `${childLabel} '${childDisplay}' is not available for ${parentLabel} '${parentDisplay}'.`,
      });
    }
  }

  return { valid: violations.length === 0, violations };
}

export interface AutoResolveResult {
  resolved: boolean;
  erp_id?: string;
  reason: "only_valid_option" | "ambiguous" | "no_valid_options";
  count?: number;
}

/**
 * Auto-resolve a dependent field when exactly 1 valid option exists.
 * Used as a fallback in the push resolution chain after persisted defaults.
 */
export function autoResolveFromRelationship(
  candidateItems: Array<{ erp_id: string; data: Record<string, unknown> }>,
  parentValue: string,
  relationship: ListRelationship
): AutoResolveResult {
  const compatible = candidateItems.filter(
    item => checkItemCompatibility(item.data, parentValue, relationship) === "compatible"
  );

  if (compatible.length === 1) {
    return { resolved: true, erp_id: compatible[0].erp_id, reason: "only_valid_option" };
  }
  if (compatible.length === 0) {
    return { resolved: false, reason: "no_valid_options", count: 0 };
  }
  return { resolved: false, reason: "ambiguous", count: compatible.length };
}
